import { useMemo, useState } from 'react'
import { useTranslation } from '@/utils/hook'
import { RiArrowRightUpLine } from '@remixicon/react'
import { HomeHeading } from './presentation'
import styles from './home.module.css'

export const TestimonialItem = ({ testimonial }) => {
  const [imageFailed, setImageFailed] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const text = testimonial.testimonial || ''
  const isLong = text.replace(/<[^>]*>/g, '').length > 100
  const initials = (testimonial.name || '')
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
  const hasSource = /^https?:\/\//i.test(testimonial.source || '')

  return (
    <figure className={styles.review}>
      <blockquote>
        <div
          className={`${styles.reviewBody} ${isLong && !expanded ? styles.reviewExcerpt : ''}`}
          dangerouslySetInnerHTML={{ __html: text }}
        />
        {isLong && (
          <button
            type="button"
            className={styles.readMore}
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Read full review'}
            <span className="sr-only"> from {testimonial.name}</span>
          </button>
        )}
      </blockquote>
      <figcaption>
        <div className={styles.author}>
          {testimonial.avatar && !imageFailed ? (
            <img
              src={testimonial.avatar}
              alt=""
              width={36}
              height={36}
              className={styles.avatar}
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span
              className={`${styles.avatar} ${styles.initials}`}
              aria-hidden="true"
            >
              {initials}
            </span>
          )}
          <div className={styles.authorText}>
            <p className={styles.authorName}>{testimonial.name}</p>
            {testimonial.title && (
              <p className={styles.authorTitle}>{testimonial.title}</p>
            )}
          </div>
        </div>
        {hasSource ? (
          <a
            className={styles.source}
            href={testimonial.source}
            target="_blank"
            rel="noreferrer"
          >
            {testimonial.platform}
            <RiArrowRightUpLine aria-hidden="true" />
            <span className="sr-only">: review by {testimonial.name}</span>
          </a>
        ) : (
          <p className={styles.source}>
            {[testimonial.platform, testimonial.date]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </figcaption>
    </figure>
  )
}

export const Testimonials = ({ testimonials }) => {
  const t = useTranslation()
  const [showAll, setShowAll] = useState(false)
  const reviews = useMemo(() => {
    const seen = new Set()
    return (testimonials || []).filter(row => {
      const key = row.id || `${row.name}:${row.testimonial}`
      if (!row.testimonial || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [testimonials])

  if (!reviews.length) return null

  return (
    <section
      id="testimonials0"
      className={styles.section}
      aria-labelledby="home-reviews-title"
    >
      <div className={styles.container}>
        <HomeHeading
          id="home-reviews-title"
          title={t('home.testimonialTitle')}
          center
        >
          {t('home.testimonialDesc')}
        </HomeHeading>
        <div id="home-reviews" className={styles.reviews}>
          {(showAll ? reviews : reviews.slice(0, 6)).map(row => (
            <TestimonialItem
              key={row.id || `${row.name}:${row.testimonial}`}
              testimonial={row}
            />
          ))}
        </div>
        {reviews.length > 6 && (
          <div className={styles.reviewActions}>
            <button
              type="button"
              className={`${styles.button} ${styles.secondary}`}
              aria-expanded={showAll}
              aria-controls="home-reviews"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? 'Show fewer reviews' : 'Read more reviews'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
